import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { CarrierField } from '@/components/shipments/CarrierField';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { InventoryItem } from '@/api/services';
import { useCreateShipment, useLocationsList, useInventoryList } from '@/api/hooks';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

interface ShipmentLineItem {
  itemId: string;
  item?: InventoryItem;
  quantity: number;
}

interface ShipmentFormData {
  originId: string;
  destinationId: string;
  carrier: string;
  carrierId: string | null;
  trackingNumber: string;
  estimatedDeparture: string;
  estimatedArrival: string;
  totalWeight: string;
  totalVolume: string;
  items: ShipmentLineItem[];
}

const emptyFormData: ShipmentFormData = {
  originId: '',
  destinationId: '',
  carrier: '',
  carrierId: null,
  trackingNumber: '',
  estimatedDeparture: '',
  estimatedArrival: '',
  totalWeight: '',
  totalVolume: '',
  items: [],
};

export default function ShipmentsNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<ShipmentFormData>(emptyFormData);

  const { data: locationsData, isLoading: locationsLoading } = useLocationsList({ limit: 500 });
  const { data: inventoryData, isLoading: inventoryLoading } = useInventoryList({ limit: 500 });
  const locations = locationsData?.data ?? [];
  const inventoryItems = inventoryData?.data ?? [];
  const loadingReferences = locationsLoading || inventoryLoading;

  const createMutation = useCreateShipment();

  const handleAddItem = () => {
    setFormData({ ...formData, items: [...formData.items, { itemId: '', quantity: 1 }] });
  };

  const handleRemoveItem = (index: number) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const handleItemChange = (
    index: number,
    field: keyof ShipmentLineItem,
    value: string | number
  ) => {
    const newItems = [...formData.items];
    if (field === 'itemId') {
      const selectedItem = inventoryItems.find((i) => i.id === value);
      newItems[index] = { ...newItems[index], itemId: value as string, item: selectedItem };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.originId) {
      toast.error('Please select an origin location');
      return;
    }
    if (!formData.destinationId) {
      toast.error('Please select a destination location');
      return;
    }
    if (formData.originId === formData.destinationId) {
      toast.error('Origin and destination must be different');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    for (const item of formData.items) {
      if (!item.itemId || item.quantity <= 0) {
        toast.error('All items must have a selected item and quantity greater than 0');
        return;
      }
    }

    createMutation.mutate(
      {
        companyId: user?.companyId || '',
        originId: formData.originId,
        destinationId: formData.destinationId,
        carrier: formData.carrier || undefined,
        carrierId: formData.carrierId ?? undefined,
        trackingNumber: formData.trackingNumber || undefined,
        estimatedDeparture: formData.estimatedDeparture || undefined,
        estimatedArrival: formData.estimatedArrival || undefined,
        totalWeight: formData.totalWeight ? parseFloat(formData.totalWeight) : undefined,
        totalVolume: formData.totalVolume ? parseFloat(formData.totalVolume) : undefined,
        items: formData.items.map((item) => ({ itemId: item.itemId, quantity: item.quantity })),
      },
      {
        onSuccess: () => {
          toast.success('Shipment created successfully');
          navigate('/shipments');
        },
      }
    );
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Create Shipment"
        description="Origin, destination, and items required to raise a movement between locations."
        actions={
          <Button variant="outline" onClick={() => navigate('/shipments')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Shipments
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Route */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Route</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="originId">Origin *</Label>
                <Select
                  value={formData.originId}
                  onValueChange={(value) => setFormData({ ...formData, originId: value })}
                  disabled={loadingReferences}
                >
                  <SelectTrigger id="originId">
                    <SelectValue placeholder="Select origin" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name} ({location.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="destinationId">Destination *</Label>
                <Select
                  value={formData.destinationId}
                  onValueChange={(value) => setFormData({ ...formData, destinationId: value })}
                  disabled={loadingReferences}
                >
                  <SelectTrigger id="destinationId">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name} ({location.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Carrier Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Carrier Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <CarrierField
                carrierId={formData.carrierId}
                carrier={formData.carrier}
                onChange={(next) => setFormData({ ...formData, ...next })}
              />
              <div className="space-y-2">
                <Label htmlFor="trackingNumber">Tracking Number</Label>
                <Input
                  id="trackingNumber"
                  value={formData.trackingNumber}
                  onChange={(e) => setFormData({ ...formData, trackingNumber: e.target.value })}
                  placeholder="Enter tracking number"
                />
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Schedule</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimatedDeparture">Est. Departure</Label>
                <Input
                  id="estimatedDeparture"
                  type="date"
                  value={formData.estimatedDeparture}
                  onChange={(e) => setFormData({ ...formData, estimatedDeparture: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimatedArrival">Est. Arrival</Label>
                <Input
                  id="estimatedArrival"
                  type="date"
                  value={formData.estimatedArrival}
                  onChange={(e) => setFormData({ ...formData, estimatedArrival: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Dimensions */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Dimensions (Optional)</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="totalWeight">Total Weight (kg)</Label>
                <Input
                  id="totalWeight"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.totalWeight}
                  onChange={(e) => setFormData({ ...formData, totalWeight: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totalVolume">Total Volume (m³)</Label>
                <Input
                  id="totalVolume"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.totalVolume}
                  onChange={(e) => setFormData({ ...formData, totalVolume: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Shipment Items */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Shipment Items *</h3>
              <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                <Plus className="mr-1 h-4 w-4" />
                Add Item
              </Button>
            </div>
            <Separator />

            {formData.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center">
                <Package className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No items added yet.</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleAddItem}
                >
                  Add First Item
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 rounded-lg border p-3">
                    <div className="flex-1 space-y-3">
                      <div className="space-y-2">
                        <Label>Item</Label>
                        <Select
                          value={item.itemId}
                          onValueChange={(value) => handleItemChange(index, 'itemId', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent>
                            {inventoryItems.map((invItem) => (
                              <SelectItem key={invItem.id} value={invItem.id}>
                                {invItem.name} ({invItem.sku})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)
                          }
                        />
                      </div>
                      {item.item && (
                        <p className="text-sm text-muted-foreground">Unit: {item.item.unit}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/shipments')}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Shipment'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
